import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { arrears, buildings } from '../services/api';

const SEVERITY = {
  critical: { label: 'Critical', classes: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500' },
  high:     { label: 'High',     classes: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-500' },
  medium:   { label: 'Medium',   classes: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500' },
  upcoming: { label: 'Upcoming', classes: 'bg-blue-100 text-blue-800 border-blue-200', dot: 'bg-blue-400' },
};

function SeverityBadge({ severity }) {
  const s = SEVERITY[severity] || SEVERITY.upcoming;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium border ${s.classes}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

export default function Arrears() {
  const [rows, setRows] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [buildingId, setBuildingId] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(() => {
    setLoading(true);
    arrears.list(buildingId ? { building_id: buildingId } : {})
      .then((res) => setRows(res.data))
      .catch(() => toast.error('Failed to load arrears'))
      .finally(() => setLoading(false));
  }, [buildingId]);

  useEffect(() => {
    buildings.list().then((res) => setBuildingList(res.data)).catch(() => {});
    load();
  }, [load]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((r) => r.unit_id)));
    }
  };

  const sendReminder = async (row) => {
    if (!row.owner_email) {
      toast.error('No email address for this owner');
      return;
    }
    setSending(row.unit_id);
    try {
      await arrears.sendReminder({
        unit_id:      row.unit_id,
        owner_email:  row.owner_email,
        owner_name:   row.owner_name,
        total_owed:   row.total_owed,
        months_overdue: row.months_overdue,
        unit_number:  row.unit_number,
        building_name: row.building_name,
      });
      toast.success(`Reminder sent to ${row.owner_name}`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send reminder');
    } finally {
      setSending(null);
    }
  };

  const sendBulkReminders = async () => {
    const targets = rows.filter((r) => selected.has(r.unit_id));
    if (targets.length === 0) { toast.error('Select at least one unit'); return; }
    setBulkSending(true);
    try {
      const res = await arrears.sendBulk({ unit_ids: targets });
      toast.success(`Sent ${res.data.sent} reminders${res.data.failed?.length ? `, ${res.data.failed.length} failed` : ''}`);
      setSelected(new Set());
    } catch (e) {
      toast.error('Bulk send failed');
    } finally {
      setBulkSending(false);
    }
  };

  const totalOwed = rows.reduce((s, r) => s + r.total_owed, 0);
  const critical  = rows.filter((r) => r.severity === 'critical').length;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Arrears Report</h1>
          <p className="text-gray-500 text-sm mt-1">Outstanding strata fees by unit.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <select
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Buildings</option>
            {buildingList.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          {selected.size > 0 && (
            <button
              onClick={sendBulkReminders}
              disabled={bulkSending}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-60 transition-colors">
              {bulkSending ? 'Sending…' : `Send ${selected.size} Reminder${selected.size !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-500">Units in Arrears</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{rows.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-500">Total Outstanding</p>
          <p className="text-3xl font-bold text-red-600 mt-1">
            ${totalOwed.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-500">Critical Cases</p>
          <p className="text-3xl font-bold text-red-700 mt-1">{critical}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <svg className="w-12 h-12 text-green-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 font-medium">No outstanding arrears</p>
          <p className="text-gray-400 text-sm mt-1">All strata fees are up to date.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3">
                  <input type="checkbox"
                    checked={selected.size === rows.length && rows.length > 0}
                    onChange={toggleAll}
                    className="rounded border-gray-300" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Unit</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Owner</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Building</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Overdue</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.unit_id}
                  className={`hover:bg-gray-50 ${row.severity === 'critical' ? 'bg-red-50/30' : ''}`}>
                  <td className="px-4 py-3 text-center">
                    <input type="checkbox"
                      checked={selected.has(row.unit_id)}
                      onChange={() => toggleSelect(row.unit_id)}
                      className="rounded border-gray-300" />
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-900">#{row.unit_number}</td>
                  <td className="px-4 py-3">
                    <p className="text-gray-900">{row.owner_name}</p>
                    {row.owner_email && (
                      <p className="text-xs text-gray-400">{row.owner_email}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{row.building_name}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {row.days_overdue > 0 ? (
                      <span className="text-red-600 font-medium">{row.days_overdue}d overdue</span>
                    ) : (
                      <span className="text-blue-600">Due soon</span>
                    )}
                    <br />
                    <span className="text-xs text-gray-400">{row.months_overdue} month{row.months_overdue !== 1 ? 's' : ''}</span>
                  </td>
                  <td className="px-4 py-3 font-bold text-red-600">
                    ${row.total_owed.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3">
                    <SeverityBadge severity={row.severity} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => sendReminder(row)}
                      disabled={!row.owner_email || sending === row.unit_id}
                      title={!row.owner_email ? 'No email on file' : 'Send reminder email'}
                      className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium px-3 py-1.5 rounded-lg disabled:opacity-40 transition-colors">
                      {sending === row.unit_id ? 'Sending…' : 'Remind'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
